import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CompressIcon from '@mui/icons-material/Compress';
import AddIcon from '@mui/icons-material/Add';

interface SliderImage {
  id: string;
  imageUrl: string;
  title?: string;
  description?: string;
  order: number;
}

interface ResizeSettings {
  maxWidth: number;
  maxHeight: number;
  quality: number;
}

const ImageSliderManager = () => {
  const [images, setImages] = useState<SliderImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Resize settings
  const [resizeDialogOpen, setResizeDialogOpen] = useState(false);
  const [resizeImageId, setResizeImageId] = useState<string | null>(null);
  const [resizeSettings, setResizeSettings] = useState<ResizeSettings>({
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 92
  });
  const [resizing, setResizing] = useState(false);
  const [originalSize, setOriginalSize] = useState<number>(0);
  const [compressedSize, setCompressedSize] = useState<number>(0);

  // Utility function to resize/compress image
  const resizeImage = (
    file: File | string,
    maxWidth: number,
    maxHeight: number,
    quality: number
  ): Promise<{ base64: string; size: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with quality
        const base64 = canvas.toDataURL('image/jpeg', quality / 100);
        const size = Math.round((base64.length * 3) / 4); // Approximate size in bytes
        
        resolve({ base64, size });
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      
      if (typeof file === 'string') {
        img.src = file;
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      }
    });
  };

  // Auto-resize large images during upload
  const processAndUploadImage = async (id: string, file: File) => {
    setUploading(id);
    setUploadProgress(0);
    
    try {
      setUploadProgress(20);
      
      const fileSizeKB = file.size / 1024;
      showSnackbar(`Processing image (${fileSizeKB > 1024 ? (fileSizeKB / 1024).toFixed(2) + 'MB' : Math.round(fileSizeKB) + 'KB'})...`, 'success');
      setUploadProgress(40);

      // Always compress to ensure base64 fits within Firestore's 1MB document limit
      // Base64 is ~33% larger than binary, so we target ~700KB base64 max
      const MAX_BASE64_LENGTH = 700 * 1024; // ~700KB base64 string
      let quality = 85;
      let result = await resizeImage(file, 1920, 1080, quality);

      // If base64 is still too large, reduce quality until it fits
      while (result.base64.length > MAX_BASE64_LENGTH && quality > 30) {
        quality -= 10;
        result = await resizeImage(file, 1920, 1080, quality);
      }

      setUploadProgress(80);

      const newSizeKB = result.size / 1024;
      if (fileSizeKB > newSizeKB + 10) {
        showSnackbar(`Compressed from ${fileSizeKB > 1024 ? (fileSizeKB / 1024).toFixed(2) + 'MB' : Math.round(fileSizeKB) + 'KB'} to ${newSizeKB > 1024 ? (newSizeKB / 1024).toFixed(2) + 'MB' : Math.round(newSizeKB) + 'KB'}`, 'success');
      }
      
      handleUpdateImage(id, 'imageUrl', result.base64);
      setUploadProgress(100);
      
      setTimeout(() => {
        setUploading(null);
        setUploadProgress(0);
        showSnackbar('Image uploaded successfully. Click "Save" to store it.', 'success');
      }, 500);
      
    } catch (error: any) {
      console.error('Error processing image:', error);
      showSnackbar('Failed to process image: ' + (error.message || 'Unknown error'), 'error');
      setUploading(null);
      setUploadProgress(0);
    }
  };

  // Open resize dialog for existing image
  const openResizeDialog = (id: string) => {
    const image = images.find(img => img.id === id);
    if (image?.imageUrl) {
      setResizeImageId(id);
      // Calculate original size
      const base64Length = image.imageUrl.length;
      const sizeBytes = Math.round((base64Length * 3) / 4);
      setOriginalSize(sizeBytes);
      setCompressedSize(0);
      setResizeDialogOpen(true);
    }
  };

  // Preview compression
  const previewCompression = async () => {
    if (!resizeImageId) return;
    
    const image = images.find(img => img.id === resizeImageId);
    if (!image?.imageUrl) return;
    
    try {
      const { size } = await resizeImage(
        image.imageUrl,
        resizeSettings.maxWidth,
        resizeSettings.maxHeight,
        resizeSettings.quality
      );
      setCompressedSize(size);
    } catch (error) {
      console.error('Error previewing compression:', error);
    }
  };

  // Apply resize to existing image
  const applyResize = async () => {
    if (!resizeImageId) return;
    
    const image = images.find(img => img.id === resizeImageId);
    if (!image?.imageUrl) return;
    
    setResizing(true);
    
    try {
      const { base64 } = await resizeImage(
        image.imageUrl,
        resizeSettings.maxWidth,
        resizeSettings.maxHeight,
        resizeSettings.quality
      );
      
      handleUpdateImage(resizeImageId, 'imageUrl', base64);
      showSnackbar('Image resized successfully. Click "Save" to store it.', 'success');
      setResizeDialogOpen(false);
    } catch (error: any) {
      showSnackbar('Failed to resize image: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setResizing(false);
    }
  };

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchImages = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'slider_images'));
      const fetchedImages: SliderImage[] = [];
      querySnapshot.forEach((doc) => {
        fetchedImages.push({ id: doc.id, ...doc.data() } as SliderImage);
      });
      
      const sortedImages = fetchedImages.sort((a, b) => a.order - b.order);
      
      if (sortedImages.length > 0) {
        setImages(sortedImages);
      } else {
        // Initialize with one empty slot if nothing in Firestore
        setImages([{
          id: 'slot_0',
          imageUrl: '',
          title: '',
          description: '',
          order: 0
        }]);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      showSnackbar('Failed to load images', 'error');
      
      // Initialize with one empty slot on error
      setImages([{
        id: 'slot_0',
        imageUrl: '',
        title: '',
        description: '',
        order: 0
      }]);
    }
  };

  const handleAddImage = () => {
    const maxOrder = images.length > 0 ? Math.max(...images.map(img => img.order)) : -1;
    const newImage: SliderImage = {
      id: `slot_${Date.now()}`,
      imageUrl: '',
      title: '',
      description: '',
      order: maxOrder + 1
    };
    setImages([...images, newImage]);
  };

  const handleUpdateImage = (id: string, field: keyof SliderImage, value: string | number) => {
    setImages(images.map(img => 
      img.id === id ? { ...img, [field]: value } : img
    ));
  };

  const handleSaveImage = async (image: SliderImage) => {
    if (!image.imageUrl) {
      showSnackbar('Please add an image first', 'error');
      return;
    }
    
    setLoading(true);
    try {
      const docId = image.id.startsWith('slot_') ? `slider_${image.order}_${Date.now()}` : image.id;
      await setDoc(doc(db, 'slider_images', docId), {
        imageUrl: image.imageUrl,
        title: image.title || '',
        description: image.description || '',
        order: image.order
      });
      showSnackbar(`Slide ${image.order + 1} saved successfully`, 'success');
      await fetchImages();
    } catch (error) {
      console.error('Error saving image:', error);
      showSnackbar('Failed to save image', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    
    // If it's an unsaved slot, just remove from local state
    if (id.startsWith('slot_')) {
      setImages(prev => {
        const filtered = prev.filter(img => img.id !== id);
        return filtered.map((img, idx) => ({ ...img, order: idx }));
      });
      showSnackbar('Slide removed', 'success');
      return;
    }

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'slider_images', id));
      showSnackbar('Slide deleted successfully', 'success');
      await fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      showSnackbar('Failed to delete slide', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }

    // Use the new process and upload function that handles resizing
    await processAndUploadImage(id, file);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Image Slider Management
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {images.filter(i => i.imageUrl).length} / {images.length} slide(s) • Max 10
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddImage}
            disabled={images.length >= 10}
            sx={{ bgcolor: '#d4af37', color: '#000', '&:hover': { bgcolor: '#c9a227' }, '&:disabled': { bgcolor: '#ccc' } }}
          >
            Add New Slide
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {images.map((image, index) => (
          <Grid item xs={12} key={image.id}>
            <Paper sx={{ p: 3, border: image.order === 0 ? '3px solid #d4af37' : '2px solid #ddd', position: 'relative' }}>
              <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: image.order === 0 ? '#d4af37' : '#666', color: '#fff', px: 2, py: 0.5, borderRadius: 1, fontWeight: 700 }}>
                Slide {image.order + 1} {image.order === 0 && '(Main)'}
              </Box>
              
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  {/* Image Preview Section */}
                  <Paper sx={{ p: 2, bgcolor: '#f9f9f9', border: '2px dashed #ccc', minHeight: 250 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#666' }}>
                      Image Preview
                    </Typography>
                    {image.imageUrl ? (
                      <Box
                        component="img"
                        src={
                          image.imageUrl.startsWith('data:') 
                            ? image.imageUrl 
                            : image.imageUrl.startsWith('http') 
                              ? image.imageUrl 
                              : `${process.env.PUBLIC_URL}${image.imageUrl}`
                        }
                        alt={`Slide ${image.order + 1}`}
                        sx={{ 
                          width: '100%', 
                          height: 200, 
                          objectFit: 'cover', 
                          borderRadius: 2,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        onError={(e: any) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Box sx={{ 
                        width: '100%', 
                        height: 200, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: '#e0e0e0',
                        borderRadius: 2,
                        color: '#999'
                      }}>
                        <Typography variant="body2">No image uploaded</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  {/* Upload Section */}
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', border: '2px dashed #d4af37', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Upload Image
                    </Typography>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={uploading === image.id ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                      disabled={uploading === image.id}
                      fullWidth
                      sx={{ 
                        bgcolor: '#d4af37', 
                        color: '#000',
                        '&:hover': { bgcolor: '#e5c158' },
                        '&:disabled': { bgcolor: '#ccc' }
                      }}
                    >
                      {uploading === image.id ? `Uploading ${Math.round(uploadProgress)}%` : 'Choose & Upload Image'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleFileSelect(image.id, e)}
                        disabled={uploading === image.id}
                      />
                    </Button>
                    {uploading === image.id && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={uploadProgress} 
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'rgba(212,175,55,0.2)',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#d4af37',
                              borderRadius: 3
                            }
                          }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'center', fontWeight: 600 }}>
                          {Math.round(uploadProgress)}% uploaded
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Resize Button - Show only if image exists */}
                    {image.imageUrl && (
                      <Button
                        variant="outlined"
                        startIcon={<CompressIcon />}
                        onClick={() => openResizeDialog(image.id)}
                        fullWidth
                        sx={{ 
                          mt: 1.5,
                          borderColor: '#2196f3', 
                          color: '#2196f3',
                          '&:hover': { bgcolor: 'rgba(33,150,243,0.1)', borderColor: '#1976d2' }
                        }}
                      >
                        Resize / Compress Image
                      </Button>
                    )}
                    
                    <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
                      Large images are auto-compressed. Use Resize button for manual control.
                    </Typography>
                  </Paper>
                  
                  {/* Title & Description */}
                  <TextField
                    fullWidth
                    label="Title"
                    value={image.title || ''}
                    onChange={(e) => handleUpdateImage(image.id, 'title', e.target.value)}
                    sx={{ mb: 2 }}
                    placeholder={image.order === 0 ? "Welcome to Temple" : "Slide title"}
                  />
                  <TextField
                    fullWidth
                    label={image.order === 0 ? "Subtitle" : "Description"}
                    value={image.description || ''}
                    onChange={(e) => handleUpdateImage(image.id, 'description', e.target.value)}
                    placeholder={image.order === 0 ? "Preserving tradition. Serving devotion." : "Optional description"}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveImage(image)}
                      disabled={loading || !image.imageUrl}
                      sx={{ bgcolor: '#66bb6a', '&:hover': { bgcolor: '#4caf50' } }}
                    >
                      Save Slide {image.order + 1}
                    </Button>
                    {(image.imageUrl || !image.id.startsWith('slot_')) && (
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteImage(image.id)}
                        disabled={loading || images.length <= 1}
                        title={images.length <= 1 ? 'At least one slide is required' : 'Delete this slide'}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Resize Dialog */}
      <Dialog 
        open={resizeDialogOpen} 
        onClose={() => setResizeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#d4af37', color: '#000', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompressIcon />
            Resize / Compress Image
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Adjust the settings below to resize and compress your image. Lower quality = smaller file size.
            </Typography>
            
            {originalSize > 0 && (
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mb: 3 }}>
                <Typography variant="body2">
                  <strong>Current Size:</strong> {(originalSize / 1024).toFixed(2)} KB
                </Typography>
                {compressedSize > 0 && (
                  <Typography variant="body2" sx={{ color: 'green', mt: 0.5 }}>
                    <strong>Estimated New Size:</strong> {(compressedSize / 1024).toFixed(2)} KB 
                    ({Math.round((1 - compressedSize / originalSize) * 100)}% smaller)
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Max Width: {resizeSettings.maxWidth}px
            </Typography>
            <Slider
              value={resizeSettings.maxWidth}
              onChange={(_, value) => setResizeSettings(prev => ({ ...prev, maxWidth: value as number }))}
              onChangeCommitted={previewCompression}
              min={640}
              max={2560}
              step={80}
              marks={[
                { value: 640, label: '640' },
                { value: 1280, label: '1280' },
                { value: 1920, label: '1920' },
                { value: 2560, label: '2560' }
              ]}
              sx={{ color: '#d4af37' }}
            />
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Max Height: {resizeSettings.maxHeight}px
            </Typography>
            <Slider
              value={resizeSettings.maxHeight}
              onChange={(_, value) => setResizeSettings(prev => ({ ...prev, maxHeight: value as number }))}
              onChangeCommitted={previewCompression}
              min={360}
              max={1440}
              step={60}
              marks={[
                { value: 360, label: '360' },
                { value: 720, label: '720' },
                { value: 1080, label: '1080' },
                { value: 1440, label: '1440' }
              ]}
              sx={{ color: '#d4af37' }}
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Quality: {resizeSettings.quality}%
            </Typography>
            <Slider
              value={resizeSettings.quality}
              onChange={(_, value) => setResizeSettings(prev => ({ ...prev, quality: value as number }))}
              onChangeCommitted={previewCompression}
              min={30}
              max={100}
              step={5}
              marks={[
                { value: 30, label: '30%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' }
              ]}
              sx={{ color: '#d4af37' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Lower quality = smaller file size. 70-80% is recommended for web.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setResizeDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={previewCompression}
            variant="outlined"
            sx={{ borderColor: '#d4af37', color: '#d4af37' }}
          >
            Preview Size
          </Button>
          <Button 
            onClick={applyResize}
            variant="contained"
            disabled={resizing}
            startIcon={resizing ? <CircularProgress size={16} color="inherit" /> : <CompressIcon />}
            sx={{ bgcolor: '#d4af37', color: '#000', '&:hover': { bgcolor: '#c9a227' } }}
          >
            {resizing ? 'Resizing...' : 'Apply Resize'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageSliderManager;